import React from 'react';

interface AddressHighlightProviderProps {
  children: React.ReactNode;
  resetKey?: string;
}

interface TAddressHighlightContext {
  onMouseEnter: (event: React.MouseEvent) => void;
  onMouseLeave: (event: React.MouseEvent) => void;
  onFocus: (event: React.FocusEvent) => void;
  onBlur: (event: React.FocusEvent) => void;
}

export const AddressHighlightContext = React.createContext<TAddressHighlightContext | null>(null);

// EVM checksum casing does not change identity. Other address formats may be case-sensitive.
function normalizeHash(hash: string) {
  return /^0x[\da-f]{40}$/i.test(hash) ? hash.toLowerCase() : hash;
}

export function AddressHighlightProvider({ children, resetKey }: AddressHighlightProviderProps) {
  const parent = React.useContext(AddressHighlightContext);
  const hovered = React.useRef<Element | null>(null);
  const focused = React.useRef<Element | null>(null);
  const highlighted = React.useRef<Array<Element>>([]);

  const clear = React.useCallback(() => {
    highlighted.current.forEach(node => node.classList.remove('address-entity_highlighted'));
    highlighted.current = [];
  }, []);

  const update = React.useCallback(() => {
    clear();
    const target = hovered.current?.isConnected ? hovered.current : focused.current;
    const hash = target?.isConnected ? target.getAttribute('data-hash') : null;
    if (!hash) return;
    // Compare full identifiers, never truncated labels. Updating only matching DOM
    // nodes keeps large transaction tables from rerendering on every pointer move.
    highlighted.current = Array.from(document.querySelectorAll('[data-hash]')).filter(node => {
      const candidate = node.getAttribute('data-hash');
      return candidate && normalizeHash(candidate) === normalizeHash(hash);
    });
    highlighted.current.forEach(node => node.classList.add('address-entity_highlighted'));
  }, [ clear ]);

  const onMouseEnter = React.useCallback((event: React.MouseEvent) => {
    hovered.current = event.currentTarget;
    update();
  }, [ update ]);
  const onMouseLeave = React.useCallback((event: React.MouseEvent) => {
    if (hovered.current === event.currentTarget) hovered.current = null;
    update();
  }, [ update ]);
  const onFocus = React.useCallback((event: React.FocusEvent) => {
    focused.current = event.currentTarget;
    update();
  }, [ update ]);
  const onBlur = React.useCallback((event: React.FocusEvent) => {
    // Moving between the address link and its copy button keeps the same address active.
    if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
    if (focused.current === event.currentTarget) focused.current = null;
    update();
  }, [ update ]);

  const value = React.useMemo(() => ({ onMouseEnter, onMouseLeave, onFocus, onBlur }), [ onMouseEnter, onMouseLeave, onFocus, onBlur ]);
  React.useEffect(() => {
    hovered.current = null;
    focused.current = null;
    clear();
    return clear;
  }, [ resetKey, clear ]);

  // Existing table-level providers share the app provider so every list uses the
  // same identity and cleanup behavior, including lists on small screens.
  return <AddressHighlightContext.Provider value={ parent ?? value }>{ children }</AddressHighlightContext.Provider>;
}

export function useAddressHighlightContext(disabled?: boolean) {
  const context = React.useContext(AddressHighlightContext);
  return disabled ? null : context;
}
